import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Trans, useTranslation } from "react-i18next";
import { usePublicAuth } from "../context/PublicAuthContext";
import { Heart, Star, Bell, MessageSquare, Shield, Check, Loader2, Sparkles, Database, Users, BarChart2, ShoppingBag } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FREE_ICONS = [Database, Users, BarChart2, ShoppingBag, Heart, Shield];
const SUPPORTER_ICONS = [MessageSquare, Heart, Bell, Star, Sparkles];

export default function SupporterPage() {
  const { publicUser, setPublicUser, isPublicAuth, isPublicLoading, login, publicApi } = usePublicAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [widerrufAccepted, setWiderrufAccepted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkPendingLoading, setCheckPendingLoading] = useState(false);

  const FEATURES_FREE = [
    { icon: Database, key: "feature_database" },
    { icon: Users, key: "feature_wiki" },
    { icon: BarChart2, key: "feature_charts" },
    { icon: ShoppingBag, key: "feature_shop" },
    { icon: Heart, key: "feature_likes" },
    { icon: Shield, key: "feature_free_access" },
  ];

  const FEATURES_SUPPORTER = [
    { icon: MessageSquare, key: "feature_comments" },
    { icon: Heart, key: "feature_favorites" },
    { icon: Bell, key: "feature_follows" },
    { icon: Star, key: "feature_badge" },
    { icon: Sparkles, key: "feature_hype" },
  ];

  // Poll payment status after Stripe redirect
  useEffect(() => {
    if (!sessionId || statusChecked || !isPublicAuth) return;
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const res = await publicApi("get", `/subscriptions/status/${sessionId}`);
        const data = res.data;
        // Subscription-Mode: status === "complete" (payment_status ist "no_payment_required")
        // Payment-Mode: payment_status === "paid"
        const isSuccess = data.payment_status === "paid" || data.status === "complete";
        if (isSuccess) {
          setPaymentStatus("success");
          setStatusChecked(true);
          // User-Context aktualisieren, damit is_supporter sofort aktiv ist
          try {
            const meRes = await publicApi("get", "/me/me");
            setPublicUser(meRes.data);
          } catch {}
          return;
        }
        if (data.status === "expired" || attempts >= 8) {
          setPaymentStatus(isSuccess ? "success" : "pending");
          setStatusChecked(true);
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatusChecked(true);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, statusChecked, isPublicAuth, publicApi]);

  // Ausstehende Zahlung manuell prüfen (z.B. wenn der Redirect fehlschlug)
  const handleCheckPending = async () => {
    setCheckPendingLoading(true);
    try {
      const res = await publicApi("post", "/subscriptions/check-pending");
      if (res.data.is_supporter) {
        setPaymentStatus("success");
        const meRes = await publicApi("get", "/me/me");
        setPublicUser(meRes.data);
      } else {
        alert("Keine abgeschlossene Zahlung gefunden. Bitte wende dich an den Support.");
      }
    } catch {
      alert("Fehler beim Prüfen. Bitte versuche es später erneut.");
    } finally {
      setCheckPendingLoading(false);
    }
  };

  // Schritt 2: Checkout starten (AGB bereits akzeptiert)
  const handleCheckout = async () => {
    if (!isPublicAuth) {
      login({ appState: { returnTo: "/supporter" } });
      return;
    }
    if (!selectedPlan) return;
    setCheckoutLoading(true);
    try {
      const res = await publicApi("post", "/subscriptions/checkout", {
        plan: selectedPlan,
        origin: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Starten des Checkouts");
      setCheckoutLoading(false);
    }
  };

  const isSupporter = publicUser?.is_supporter;

  return (
    <>
      <Helmet>
        <title>Supporter werden | HUWND</title>
        <meta name="description" content="Unterstütze die HUWND Community und erhalte exklusive Features: Gespeicherte Releases, Artist-Following, Hype-Votes und mehr." />
        <link rel="canonical" href="https://handsupwillneverdie.de/supporter" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:title" content="Supporter werden | HUWND" />
        <meta property="og:description" content="Unterstütze die HUWND Community und erhalte exklusive Supporter-Features." />
        <meta property="og:url" content="https://handsupwillneverdie.de/supporter" />
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="min-h-screen bg-zinc-950">
        {/* Success Banner */}
        {paymentStatus === "success" && (
          <div className="bg-emerald-600/20 border-b border-emerald-500/30 py-4 px-4 text-center">
            <p className="text-emerald-400 font-bold flex items-center justify-center gap-2">
              <Check size={16} /> {t("supporter_page.payment_success_desc")}
            </p>
          </div>
        )}

        {/* Hero */}
        <div className="relative pt-20 pb-16 px-4 text-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(220,38,38,0.08)_0%,_transparent_70%)] pointer-events-none" />
          <div className="relative max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-rose-500/10 border border-rose-500/20 rounded-full px-4 py-1.5 text-rose-400 text-xs font-bold uppercase tracking-widest mb-6">
              <Sparkles size={12} /> HUWND Supporter
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white font-heading leading-tight mb-4">
              {t("supporter_page.title")}
            </h1>
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
              {t("supporter_page.subtitle")}
            </p>
          </div>
        </div>

        {/* Pricing */}
        {!isSupporter && (
          <div className="max-w-3xl mx-auto px-4 pb-20">

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {/* Monatlich */}
              <button
                type="button"
                onClick={() => setSelectedPlan("monthly")}
                data-testid="plan-monthly"
                className={`text-left bg-zinc-900 rounded-2xl p-6 flex flex-col border-2 transition-all duration-200 ${
                  selectedPlan === "monthly"
                    ? "border-white/40 ring-1 ring-white/20"
                    : "border-white/5 hover:border-white/15"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t("supporter_page.plan_monthly")}</p>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    selectedPlan === "monthly" ? "border-white bg-white" : "border-zinc-600"
                  }`}>
                    {selectedPlan === "monthly" && <Check size={11} className="text-zinc-950" />}
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-black text-white">3€</span>
                  <span className="text-zinc-500 text-sm">/ {t("common.monthly").toLowerCase()}</span>
                </div>
                <p className="text-zinc-600 text-xs mb-4">{t("supporter_page.plan_monthly_price")}</p>
                <ul className="space-y-1.5">
                  {FEATURES_SUPPORTER.map((f) => (
                    <li key={f.key} className="flex items-start gap-2 text-xs text-zinc-400">
                      <Check size={11} className="text-rose-500 mt-0.5 flex-shrink-0" />
                      {t(`supporter_page.${f.key}`)}
                    </li>
                  ))}
                </ul>
              </button>

              {/* Jährlich */}
              <button
                type="button"
                onClick={() => setSelectedPlan("yearly")}
                data-testid="plan-yearly"
                className={`text-left bg-zinc-900 rounded-2xl p-6 flex flex-col border-2 relative transition-all duration-200 ${
                  selectedPlan === "yearly"
                    ? "border-rose-500/70 ring-1 ring-rose-500/30"
                    : "border-rose-500/30 hover:border-rose-500/50"
                }`}
              >
                <div className="absolute top-4 right-4 bg-rose-600 text-white text-xs font-black px-2 py-0.5 rounded-full uppercase tracking-wide">
                  {t("supporter_page.plan_yearly_savings")}
                </div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t("supporter_page.plan_yearly")}</p>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    selectedPlan === "yearly" ? "border-rose-500 bg-rose-600" : "border-zinc-600"
                  }`}>
                    {selectedPlan === "yearly" && <Check size={11} className="text-white" />}
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-black text-white">20€</span>
                  <span className="text-zinc-500 text-sm">/ {t("common.yearly").toLowerCase()}</span>
                </div>
                <p className="text-emerald-400 text-xs mb-4 font-semibold">{t("supporter_page.plan_yearly_savings")}</p>
                <ul className="space-y-1.5">
                  {FEATURES_SUPPORTER.map((f) => (
                    <li key={f.key} className="flex items-start gap-2 text-xs text-zinc-400">
                      <Check size={11} className="text-rose-500 mt-0.5 flex-shrink-0" />
                      {t(`supporter_page.${f.key}`)}
                    </li>
                  ))}
                </ul>
              </button>
            </div>

            {/* Schritt 2: Bestätigen */}
            {selectedPlan && isPublicAuth && (
              <div className="bg-zinc-900/80 border border-white/8 rounded-2xl p-5 mb-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold text-sm">
                      {selectedPlan === "monthly" ? `${t("supporter_page.plan_monthly")} · 3€ / ${t("common.monthly").toLowerCase()}` : `${t("supporter_page.plan_yearly")} · 20€ / ${t("common.yearly").toLowerCase()}`}
                    </p>
                    <p className="text-zinc-500 text-xs mt-0.5">
                      {selectedPlan === "monthly" ? "Jederzeit kündbar" : "Entspricht 1,67€ / Monat · 2 Monate gratis"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedPlan(null)}
                    className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors"
                  >
                    {t("common.cancel")}
                  </button>
                </div>

                {/* AGB beim Bestätigen */}
                <div className="space-y-2.5 pt-1 border-t border-white/5">
                  <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="agb-checkbox-label">
                    <input
                      type="checkbox"
                      checked={agbAccepted}
                      onChange={e => setAgbAccepted(e.target.checked)}
                      data-testid="agb-checkbox"
                      className="mt-0.5 accent-rose-600 w-4 h-4 flex-shrink-0 cursor-pointer"
                    />
                    <span className="text-xs text-zinc-500 leading-relaxed group-hover:text-zinc-400 transition-colors">
                      {t("supporter_page.agb_agree", {
                        agb: (chunks) => <Link to="/page/agb" target="_blank" className="text-rose-400 hover:text-rose-300 underline underline-offset-2">{chunks}</Link>
                      })}
                      {/* Fallback inline */}
                      {" "}<Link to="/page/agb" target="_blank" className="text-rose-400 hover:text-rose-300 underline underline-offset-2 sr-only">AGB</Link>
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="widerruf-checkbox-label">
                    <input
                      type="checkbox"
                      checked={widerrufAccepted}
                      onChange={e => setWiderrufAccepted(e.target.checked)}
                      data-testid="widerruf-checkbox"
                      className="mt-0.5 accent-rose-600 w-4 h-4 flex-shrink-0 cursor-pointer"
                    />
                    <span className="text-xs text-zinc-500 leading-relaxed group-hover:text-zinc-400 transition-colors">
                      {t("supporter_page.widerruf_agree")}
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={checkoutLoading || !agbAccepted || !widerrufAccepted}
                  data-testid="checkout-confirm-btn"
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold rounded-xl transition-colors"
                >
                  {checkoutLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {t("supporter_page.start_subscription")}
                </button>
              </div>
            )}

            {/* Nicht eingeloggt – zeige CTA */}
            {selectedPlan && !isPublicAuth && (
              <div className="bg-zinc-900/80 border border-white/8 rounded-2xl p-5 mb-6 text-center">
                <p className="text-zinc-300 text-sm mb-3">{t("supporter_page.login_to_continue")}</p>
                <button
                  type="button"
                  onClick={() => login({ appState: { returnTo: "/supporter" } })}
                  className="px-6 py-2.5 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-xl transition-colors text-sm"
                >
                  {t("supporter_page.login_cta")}
                </button>
              </div>
            )}

            {/* Kleinunternehmerregelung */}
            <p className="text-center text-zinc-700 text-xs mb-6">
              Alle Preise in Euro. Gemäß §&nbsp;19 UStG wird keine Mehrwertsteuer berechnet (Kleinunternehmerregelung).
            </p>

            {/* Self-repair */}
            {isPublicAuth && !sessionId && (
              <div className="text-center mb-8">
                <p className="text-zinc-600 text-xs mb-1.5">Bereits bezahlt aber kein Supporter-Status?</p>
                <button
                  type="button"
                  onClick={handleCheckPending}
                  disabled={checkPendingLoading}
                  data-testid="check-pending-btn"
                  className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2 transition-colors disabled:opacity-50"
                >
                  {checkPendingLoading ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
                  {t("supporter_page.check_payment")}
                </button>
              </div>
            )}

            {/* Free tier comparison */}
            <div className="text-center mb-10">
              <p className="text-zinc-500 text-sm mb-4 uppercase tracking-wider font-semibold">{t("supporter_page.free_features")}</p>
              <div className="flex flex-wrap justify-center gap-4">
                {FEATURES_FREE.map((f) => (
                  <div key={f.key} className="flex items-center gap-2 text-zinc-400 text-sm">
                    <f.icon size={14} className="text-zinc-600" />
                    {t(`supporter_page.${f.key}`)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Already supporter */}
        {isSupporter && (
          <div className="max-w-lg mx-auto px-4 pb-20 text-center">
            <div className="bg-zinc-900 border border-rose-500/30 rounded-2xl p-8">
              <div className="w-16 h-16 bg-rose-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart size={28} className="text-rose-500 fill-rose-500" />
              </div>
              <h2 className="text-2xl font-black text-white mb-2">{t("supporter_page.already_supporter")}</h2>
              <p className="text-zinc-400 text-sm mb-6">{t("supporter_page.already_supporter_desc")}</p>
              <Link to="/profil" className="inline-block px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold rounded-xl transition-colors">
                {t("header.my_profile")}
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
